import React, {
    useEffect,
    useState,
} from 'react'
import { useSelector } from 'react-redux'

import { readDeviceOilHistory } from '../oil-history-helpers'
import OilLevelHistoryChart from './oil-level-history-chart'
import { setDeviceOilHistoryTimeRangeAction } from '../../../reducers/actions/device-oil-history'
import { getMonthsRange } from '../../../components/shared/months-range'

interface OilHistoryItem {
    createdAt: string | number
    formattedCreatedAt: string
    oilLevelPercentage: number
}

interface RawOilHistoryItem {
    createdAt: string | number
    oilLevelPercentage?: number
}

interface DeviceEntity {
    uid?: string
}

interface DeviceOilLevelHistoryProps {
    device?: DeviceEntity | null
}

interface DeviceOilHistoryState {
    from: string | number
    to: string | number
}

interface RootState {
    deviceOilHistory: DeviceOilHistoryState
}

/**
 * Container that loads and filters the device oil level history
 * and passes it down to OilLevelHistoryChart.
 */
const DeviceOilLevelHistory = ({
    device = null,
}: DeviceOilLevelHistoryProps) => {
    const [deviceOilHistory, setDeviceOilHistory] = useState<OilHistoryItem[]>([])
    const oilHistoryFrom = useSelector((state: RootState) => state.deviceOilHistory.from)
    const oilHistoryTo = useSelector((state: RootState) => state.deviceOilHistory.to)

    useEffect(() => {
        const monthsRange = getMonthsRange()
        setDeviceOilHistoryTimeRangeAction(monthsRange[0].start, monthsRange[0].end)
    }, [])

    useEffect(() => {
        if (device?.uid) {
            readDeviceOilHistory(device.uid, Number(oilHistoryFrom), Number(oilHistoryTo))
                .then((data: RawOilHistoryItem[]) => {
                    const usedDays: string[] = []
                    const dataForGraph: OilHistoryItem[] = [ ...data ]
                        .map(dataItem => ({
                            createdAt: dataItem.createdAt,
                            oilLevelPercentage: dataItem.oilLevelPercentage ?? 0,
                            formattedCreatedAt: window.moment.utc(dataItem.createdAt).local().format('D.M.YYYY'),
                        }))
                        .reverse()
                        .filter(oilHistory => {
                            const oilHistoryDay = window.moment
                                .utc(oilHistory.createdAt)
                                .local()
                                .format('L')

                            if (usedDays.includes(oilHistoryDay)) {
                                return false
                            }

                            usedDays.push(oilHistoryDay)
                            return true
                        })

                    setDeviceOilHistory(dataForGraph)
                })
        }
    }, [
        oilHistoryTo,
        oilHistoryFrom,
        device?.uid,
    ])

    return (
        <div className="mt-3 d-flex flex-row flex-wrap">
            <OilLevelHistoryChart
                oilHistoryFrom={oilHistoryFrom}
                oilHistoryTo={oilHistoryTo}
                deviceOilHistory={deviceOilHistory}
            />
        </div>
    )
}

export default DeviceOilLevelHistory
